import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { useSettings } from "../apps/publishing/context/SettingsContext";
import ScrollToTop from "../shared/helpers/ScrollToTop";

/* -----------------------------------------
   Publishing Public Layout
------------------------------------------ */

import NavBar from "../apps/publishing/components/layout/NavBar";
import PublishingFooter from "../apps/publishing/components/layout/PublishingFooter";
import PublishingAnalytics from "../apps/publishing/components/PublishingAnalytics";

/* -----------------------------------------
   Publishing Public Pages
------------------------------------------ */

import HomePage from "../apps/publishing/pages/HomePage";
import BooksPage from "../apps/publishing/pages/BooksPage";
import BookDetail from "../apps/publishing/components/books/BookDetailCard";
import BlogPage from "../apps/publishing/pages/BlogPage";
import Blog from "../apps/publishing/pages/Blog";
import AboutPage from "../apps/publishing/pages/AboutPage";
import Contact from "../apps/publishing/pages/ContactPage";
import ShopPage from "../apps/publishing/pages/ShopPage";
import ProductPage from "../apps/publishing/pages/ProductPage";
import Terms from "../apps/publishing/pages/TermsPage";
import Privacy from "../apps/publishing/pages/PrivacyPage";
import NewsletterUnsubscribe from "../apps/publishing/components/newsletter/NewsletterUnsubscribe";
import AlreadyDownloadedPage from "../apps/publishing/pages/AlreadyDownloadedPage";

/* -----------------------------------------
   Publishing Authentication
------------------------------------------ */

import LoginPage from "../apps/publishing/admin/pages/LoginPage";
import ResetPassword from "../apps/publishing/admin/pages/ResetPassword";
import PrivateAdminRoute from "../apps/publishing/admin/components/PrivateAdminRoute";

/* -----------------------------------------
   Publishing Admin Layout
------------------------------------------ */

import AdminLayout from "../apps/publishing/admin/components/AdminLayout";
import AdminSidebar from "../apps/publishing/admin/components/AdminSidebar";

/* -----------------------------------------
   Publishing Admin Pages
------------------------------------------ */

import Books from "../apps/publishing/admin/pages/AdminBooks";
import Blogs from "../apps/publishing/admin/pages/AdminBlogs";
import AdminTestimonials from "../apps/publishing/admin/pages/AdminTestimonials";
import SocialPosts from "../apps/publishing/admin/pages/AdminSocialPosts";
import Newsletter from "../apps/publishing/admin/pages/AdminNewsletter";
import AdminImages from "../apps/publishing/admin/pages/AdminImages";
import Settings from "../apps/publishing/admin/pages/AdminSettings";
import Sales from "../apps/publishing/admin/pages/AdminSales";
import AdminPublishingDashboard from "../apps/publishing/admin/pages/AdminPublishingDashboard";
import AdminAnalytics from "../apps/publishing/admin/pages/AdminAnalytics";
import AdminPublishingQueue from "../apps/publishing/admin/pages/AdminPublishingQueue";
import CronJobs from "../apps/publishing/admin/pages/AdminCronJobs";
import AdminMerch from "../apps/publishing/admin/pages/AdminMerch";
import AdminOrders from "../apps/publishing/admin/pages/AdminOrders";
import Marketplaces from "../apps/publishing/admin/pages/MarketplacesPage";
import AdminNewsletterHistory from "../apps/publishing/admin/pages/AdminNewsletterHistory";
import AdminNewsletterSubscribers from "../apps/publishing/admin/pages/AdminNewsletterSubscribers";
import AdminEmailTemplates from "../apps/publishing/admin/pages/AdminEmailTemplates";
import AdminEmailHistory from "../apps/publishing/admin/pages/AdminEmailHistory";
import AdminAutoSocialPosts from "../apps/publishing/admin/pages/AdminAutoSocialPosts";
import AdminCoupons from "../apps/publishing/admin/pages/AdminCoupons";
import AdminBookstores from "../apps/publishing/admin/pages/AdminBookstores";
import AdminBookstoreDetail from "../apps/publishing/admin/pages/AdminBookstoreDetail";
import AdminBookstorePricing from "../apps/publishing/admin/pages/AdminBookstorePricing";
import AdminAudiobookReviewers from "../apps/publishing/admin/pages/AdminAudiobookReviewers";

/* -----------------------------------------
   Avwakin Development
------------------------------------------ */

import AvwakinDevelopmentPage from "../apps/development/pages/AvwakinDevelopmentPage";
import AvwakinNavBar from "../apps/development/components/AvwakinNavBar";
import AvwakinFooter from "../apps/development/components/AvwakinFooter";
import DevelopmentAdminLayout from "../apps/development/admin/components/DevelopmentAdminLayout";
import DevelopmentDashboard from "../apps/development/admin/pages/DevelopmentDashboard";
import DevelopmentComingSoon from "../apps/development/admin/pages/DevelopmentComingSoon";
import DevelopmentClients from "../apps/development/admin/pages/DevelopmentClients";
import DevelopmentProjects from "../apps/development/admin/pages/DevelopmentProjects";
import DevelopmentServices from "../apps/development/admin/pages/DevelopmentServices";
import DevelopmentProposals from "../apps/development/admin/pages/DevelopmentProposals";
import DevelopmentInvoices from "../apps/development/admin/pages/DevelopmentInvoices";
import DevelopmentPayments from "../apps/development/admin/pages/DevelopmentPayments";
import DevelopmentDocuments from "../apps/development/admin/pages/DevelopmentDocuments";
import DevelopmentProjectFiles from "../apps/development/admin/pages/DevelopmentProjectFiles";
import DevelopmentPortfolio from "../apps/development/admin/pages/DevelopmentPortfolio";
import DevelopmentSettings from "../apps/development/admin/pages/DevelopmentSettings";

/* -----------------------------------------
   FourH
------------------------------------------ */

import FourHHomePage from "../apps/fourh/pages/HomePage";
import TestPage from "../apps/fourh/pages/TestPage";
import CoordinatorPage from "../apps/fourh/pages/CoordinatorPage";
import CoordinatorConsole from "../apps/fourh/pages/CoordinatorConsole";
import AdminPage from "../apps/fourh/pages/AdminPage";

import FourHHeader from "../apps/fourh/components/FourHHeader";
import FourHFooter from "../apps/fourh/components/FourHFooter";

/* -----------------------------------------
   Helpers
------------------------------------------ */

const isRouteEnabled = (value) =>
  String(value).toLowerCase() === "true";

function ProtectedAdminPage({ children }) {
  return (
    <PrivateAdminRoute>
      <AdminLayout>{children}</AdminLayout>
    </PrivateAdminRoute>
  );
}

function ProtectedDevelopmentAdminPage({
  children,
}) {
  return (
    <PrivateAdminRoute>
      <DevelopmentAdminLayout>
        {children}
      </DevelopmentAdminLayout>
    </PrivateAdminRoute>
  );
}

function FourHRoutes() {
  return (
    <Routes>
      <Route
        index
        element={<FourHHomePage />}
      />

      <Route
        path="test/:userId"
        element={<TestPage />}
      />

      <Route
        path="coordinator"
        element={<CoordinatorPage />}
      />

      <Route
        path="coordinator/:id"
        element={<CoordinatorConsole />}
      />

      <Route
        path="admin/:id"
        element={<AdminPage />}
      />

      <Route
        path="*"
        element={
          <Navigate
            to="/fourh"
            replace
          />
        }
      />
    </Routes>
  );
}

/* -----------------------------------------
   Main Application Routes
------------------------------------------ */

export default function AppRoutes() {
  const {
    settings,
    isLoading,
    error,
  } = useSettings();

  const location = useLocation();

  const normalizedPath =
    location.pathname.toLowerCase();

  const isAvwakinRoute =
    normalizedPath.startsWith(
      "/avwakindevelopment"
    );
  const isDevelopmentAdminRoute =
    normalizedPath.startsWith(
      "/avwakindevelopment/admin"
    );

  const isDevelopmentPublicRoute = isAvwakinRoute && !isDevelopmentAdminRoute;

  const isFourHRoute =
    normalizedPath.startsWith("/fourh");

  const isPublishingRoute =
    !isAvwakinRoute && !isFourHRoute;

  const isAdminPath =
    normalizedPath.startsWith("/admin");

  const shopEnabled = isRouteEnabled(
    settings?.route_shop_enabled
  );

  const fourHEnabled = isRouteEnabled(
    settings?.route_fourh_enabled
  );

  const routeShellClass = isFourHRoute
    ? "appRouteShell fourHRouteShell"
    : isAvwakinRoute
      ? "appRouteShell developmentRouteShell"
      : "appRouteShell publishingRouteShell";

  const mainContentClass = isFourHRoute
    ? "main-content fourHMainContent"
    : isAvwakinRoute
      ? "main-content developmentMainContent"
      : "main-content publishingMainContent";

  if (isLoading) {
    return (
      <div className={routeShellClass}>
        <div
          className="app-loading-message"
          role="status"
          aria-live="polite"
        >
          Loading site...
        </div>
      </div>
    );
  }

  return (
    <div className={routeShellClass}>
      <ScrollToTop />
      {isPublishingRoute && !isAdminPath && <PublishingAnalytics />}

      {isPublishingRoute && <NavBar />}

      {isDevelopmentPublicRoute && (
        <AvwakinNavBar />
      )}

      {isFourHRoute && (
        <FourHHeader />
      )}

      <main className={mainContentClass}>
        {error && isAdminPath && (
          <div
            className="warningError"
            role="alert"
          >
            {error}
          </div>
        )}

        <Routes>
          {/* ---------------------------------
              Publishing Public Routes
          ---------------------------------- */}

          <Route
            path="/"
            element={<HomePage />}
          />

          <Route
            path="/books"
            element={<BooksPage />}
          />

          <Route
            path="/books/:title"
            element={<BookDetail />}
          />

          <Route
            path="/blog"
            element={<BlogPage />}
          />

          <Route
            path="/blog/:title"
            element={<Blog />}
          />

          <Route
            path="/about"
            element={<AboutPage />}
          />

          <Route
            path="/contact"
            element={<Contact />}
          />

          <Route
            path="/terms"
            element={<Terms />}
          />

          <Route
            path="/privacy"
            element={<Privacy />}
          />

          <Route
            path="/newsletter/unsubscribe"
            element={
              <NewsletterUnsubscribe />
            }
          />
          <Route path="/newsletter/already-downloaded" element={<AlreadyDownloadedPage />} />

          <Route
            path="/login"
            element={<LoginPage />}
          />

          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />

          {/* ---------------------------------
              Publishing Shop Routes
          ---------------------------------- */}

          <Route
            path="/shop"
            element={
              shopEnabled ? (
                <ShopPage />
              ) : (
                <Navigate
                  to="/"
                  replace
                />
              )
            }
          />

          <Route
            path="/shop/p/:id"
            element={
              shopEnabled ? (
                <ProductPage />
              ) : (
                <Navigate
                  to="/"
                  replace
                />
              )
            }
          />

          {/* ---------------------------------
              FourH Routes
          ---------------------------------- */}

          <Route
            path="/fourh/*"
            element={
              fourHEnabled ? (
                <FourHRoutes />
              ) : (
                <Navigate
                  to="/"
                  replace
                />
              )
            }
          />

          {/* ---------------------------------
              Avwakin Development Route
          ---------------------------------- */}

          <Route
            path="/avwakindevelopment"
            element={
              <AvwakinDevelopmentPage />
            }
          />
          <Route
            path="/avwakindevelopment/admin"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentDashboard />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/clients"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentClients />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/projects"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentProjects />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/services"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentServices />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/proposals"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentProposals />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/invoices"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentInvoices />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/payments"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentPayments />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/documents"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentDocuments />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/files"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentProjectFiles />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/portfolio"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentPortfolio />
              </ProtectedDevelopmentAdminPage>
            }
          />

          <Route
            path="/avwakindevelopment/admin/settings"
            element={
              <ProtectedDevelopmentAdminPage>
                <DevelopmentSettings />
              </ProtectedDevelopmentAdminPage>
            }
          />

          {/* ---------------------------------
              Publishing Admin Routes
          ---------------------------------- */}

          <Route
            path="/admin"
            element={
              <ProtectedAdminPage>
                <AdminSidebar />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/sales"
            element={
              <ProtectedAdminPage>
                <Sales />
              </ProtectedAdminPage>
            }
          />
          <Route path="/admin/sales/:platform" element={<ProtectedAdminPage><Sales /></ProtectedAdminPage>} />
          <Route path="/admin/dashboard" element={<ProtectedAdminPage><AdminPublishingDashboard /></ProtectedAdminPage>} />
          <Route path="/admin/analytics" element={<ProtectedAdminPage><AdminAnalytics /></ProtectedAdminPage>} />
          <Route path="/admin/marketing" element={<ProtectedAdminPage><AdminPublishingQueue /></ProtectedAdminPage>} />

          <Route
            path="/admin/books"
            element={
              <ProtectedAdminPage>
                <Books />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/blogs"
            element={
              <ProtectedAdminPage>
                <Blogs />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/testimonials"
            element={
              <ProtectedAdminPage>
                <AdminTestimonials />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/social-posts"
            element={
              <ProtectedAdminPage>
                <SocialPosts />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/auto-social-posts"
            element={
              <ProtectedAdminPage>
                <AdminAutoSocialPosts />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/newsletter"
            element={
              <ProtectedAdminPage>
                <Newsletter />
              </ProtectedAdminPage>
            }
          />

          <Route path="/admin/bookstores" element={<ProtectedAdminPage><AdminBookstores /></ProtectedAdminPage>} />
          <Route path="/admin/audiobook-reviewers" element={<ProtectedAdminPage><AdminAudiobookReviewers /></ProtectedAdminPage>} />
          <Route path="/admin/bookstores/pricing" element={<ProtectedAdminPage><AdminBookstorePricing /></ProtectedAdminPage>} />
          <Route path="/admin/bookstores/:id" element={<ProtectedAdminPage><AdminBookstoreDetail /></ProtectedAdminPage>} />

          <Route
            path="/admin/newsletter-history"
            element={
              <ProtectedAdminPage>
                <AdminNewsletterHistory />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/subscribers"
            element={
              <ProtectedAdminPage>
                <AdminNewsletterSubscribers />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/images"
            element={
              <ProtectedAdminPage>
                <AdminImages />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/email-templates"
            element={
              <ProtectedAdminPage>
                <AdminEmailTemplates />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/email-history"
            element={
              <ProtectedAdminPage>
                <AdminEmailHistory />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/settings"
            element={
              <ProtectedAdminPage>
                <Settings />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/cron-jobs"
            element={
              <ProtectedAdminPage>
                <CronJobs />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/merch"
            element={
              <ProtectedAdminPage>
                <AdminMerch />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/orders"
            element={
              <ProtectedAdminPage>
                <AdminOrders />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/marketplaces"
            element={
              <ProtectedAdminPage>
                <Marketplaces />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/coupons"
            element={
              <ProtectedAdminPage>
                <AdminCoupons />
              </ProtectedAdminPage>
            }
          />

          {/* ---------------------------------
              Catch-All Route
          ---------------------------------- */}

          <Route
            path="*"
            element={
              <Navigate
                to="/"
                replace
              />
            }
          />
        </Routes>
      </main>

      {isPublishingRoute && (
        <PublishingFooter />
      )}

      {isDevelopmentPublicRoute && (
        <AvwakinFooter />
      )}

      {isFourHRoute && (
        <FourHFooter />
      )}
    </div>
  );
}
